import {
  formatDateYMD,
  getMonth,
  setMonth,
  startOfDay,
} from "../../../../src/shared/utils/date";

const CSV_TOKEN_RE = /"(?:""|[^"])*"|[^,\r\n]+|,|\r\n|\n|\r/g;

export const unquote = (t: string): string =>
  t.startsWith('"') ? t.slice(1, -1).replace(/""/g, '"') : t;

export const csvEscape = (s: string): string =>
  /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;

export const pushRowIfNotEmpty = (rows: string[][], row: string[]): void => {
  if (!(row.length === 1 && row[0] === "")) rows.push(row);
};

export const val = (row: string[], i: number): string =>
  i >= 0 ? String(row[i] ?? "") : "";

export const isBlob = (x: unknown): x is Blob =>
  typeof Blob !== "undefined" && x instanceof Blob;

export const formatDate = (d?: Date | null): string =>
  d ? formatDateYMD(d) : "";

export const getInitials = (name?: string): string =>
  (name ?? "")
    .split(" ")
    .map((s) => s.trim()[0])
    .filter(Boolean)
    .slice(0, 3)
    .join("")
    .toUpperCase();

export const readCompanyKey = (company: unknown): string | undefined => {
  if (!company || typeof company !== "object") return undefined;
  const r = company as Record<string, unknown>;
  const candidates = ["companyKey", "companyIdentifier", "companyCode", "key"];
  for (const k of candidates) {
    const v = r[k];
    if (typeof v === "string" && v.trim().length > 0) return v;
  }

  return undefined;
};

export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";

  const tokens = text.match(CSV_TOKEN_RE) ?? [];
  for (const tok of tokens) {
    if (tok === ",") {
      row.push(field);
      field = "";
    } else if (tok === "\r\n" || tok === "\n" || tok === "\r") {
      row.push(field);
      field = "";
      pushRowIfNotEmpty(rows, row);
      row = [];
    } else {
      field += unquote(tok);
    }
  }

  row.push(field);
  pushRowIfNotEmpty(rows, row);

  return rows;
}

export function toUtcYmdHms(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");

  return `${y}-${m}-${day} ${hh}:${mm}:${ss}`;
}

export function formatUtcTimestamp(raw: string): string {
  const s = raw.replace(/^\uFEFF/, "").trim();

  // ISO-ish with T separator: 2026-01-21T09:10:21 or 2026-01-21T09:10:21Z
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    // Check if it already has a timezone indicator
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(s);
    const isoString = hasTimezone ? s : `${s}Z`;
    const isoDate = new Date(isoString);
    if (!Number.isNaN(isoDate.getTime())) {
      return toUtcYmdHms(isoDate);
    }
  }

  // ISO-ish with space separator: 2026-01-21 09:10:21
  // The API returns timestamps in UTC but without a timezone indicator
  // We need to treat these as UTC, not local time
  const isoSpaceMatch = s.match(
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/,
  );
  if (isoSpaceMatch) {
    const [, yy, mm, dd, hh, min, ss] = isoSpaceMatch;

    // Treat the timestamp as already being in UTC
    return `${yy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  }

  // US: MM/DD/YYYY HH:mm:ss
  let m = s.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (m) {
    const mm = Number(m[1]);
    const dd = Number(m[2]);
    const yy = Number(m[3]);
    const hh = Number(m[4] ?? "0");
    const min = Number(m[5] ?? "0");
    const ss = Number(m[6] ?? "0");

    return toUtcYmdHms(new Date(Date.UTC(yy, mm - 1, dd, hh, min, ss)));
  }

  // EU: DD.MM.YYYY HH:mm:ss
  m = s.match(
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (m) {
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yy = Number(m[3]);
    const hh = Number(m[4] ?? "0");
    const min = Number(m[5] ?? "0");
    const ss = Number(m[6] ?? "0");

    return toUtcYmdHms(new Date(Date.UTC(yy, mm - 1, dd, hh, min, ss)));
  }

  const fallbackDate = new Date(s);

  return Number.isNaN(fallbackDate.getTime()) ? "" : toUtcYmdHms(fallbackDate);
}

export function monthsAgoBase(date: Date, months: number): Date {
  const x = new Date(date);
  setMonth(x, getMonth(x) - months);

  return startOfDay(x);
}
